// src/types/user.ts
var USER_ROLES = ["ADMIN", "USER", "VIEWER"];
var WRITE_ROLES = ["ADMIN", "USER"];
var BEREICHE = [
  "STAMMDATEN",
  "ABRECHNUNG",
  "AUSWERTUNG",
  "KANZLEI",
  "COMPLIANCE",
  "SYSTEM"
];
var BEREICH_LABEL = {
  STAMMDATEN: "Stammdaten",
  ABRECHNUNG: "Abrechnung",
  AUSWERTUNG: "Auswertung",
  KANZLEI: "Kanzlei",
  COMPLIANCE: "Compliance",
  SYSTEM: "System"
};

// src/validation/deutsche-fehlermeldungen.ts
import { z } from "zod";
var MAX_AUFZAEHLUNG = 8;
var zahl = (n) => typeof n === "bigint" ? n.toString() : n.toLocaleString("de-DE");
var anzahl = (n, einzahl, mehrzahl) => `${zahl(n)} ${Number(n) === 1 ? einzahl : mehrzahl}`;
var TYP_ERWARTET = {
  string: "Text erwartet",
  number: "Zahl erwartet",
  integer: "Ganzzahl erwartet",
  float: "Zahl erwartet",
  boolean: "Ja oder Nein erwartet",
  date: "Datum erwartet",
  array: "Liste erwartet",
  object: "Objekt erwartet",
  bigint: "Ganzzahl erwartet",
  nan: "Zahl erwartet"
};
var STRING_FORMAT = {
  email: "Ung\xFCltige E-Mail-Adresse",
  url: "Ung\xFCltige Internetadresse",
  uuid: "Ung\xFCltige Kennung",
  cuid: "Ung\xFCltige Kennung",
  cuid2: "Ung\xFCltige Kennung",
  ulid: "Ung\xFCltige Kennung",
  datetime: "Ung\xFCltiges Datum mit Uhrzeit",
  date: "Ung\xFCltiges Datum",
  time: "Ung\xFCltige Uhrzeit",
  ip: "Ung\xFCltige IP-Adresse"
};
var deutscheFehlerKarte = (issue) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      if (issue.received === "undefined" || issue.received === "null")
        return { message: "Pflichtfeld" };
      return { message: TYP_ERWARTET[issue.expected] ?? "Falscher Datentyp" };
    case z.ZodIssueCode.invalid_literal:
      return { message: "Ung\xFCltiger Wert" };
    case z.ZodIssueCode.unrecognized_keys:
      return { message: "Unbekannte Felder in der Eingabe" };
    case z.ZodIssueCode.invalid_union:
    case z.ZodIssueCode.invalid_union_discriminator:
      return { message: "Ung\xFCltige Eingabe" };
    case z.ZodIssueCode.invalid_enum_value: {
      const erlaubt = issue.options.map(String);
      return {
        message: erlaubt.length > 0 && erlaubt.length <= MAX_AUFZAEHLUNG ? `Ung\xFCltige Auswahl. Erlaubt: ${erlaubt.join(", ")}` : "Ung\xFCltige Auswahl"
      };
    }
    case z.ZodIssueCode.invalid_arguments:
    case z.ZodIssueCode.invalid_return_type:
      return { message: "Ung\xFCltige Eingabe" };
    case z.ZodIssueCode.invalid_date:
      return { message: "Ung\xFCltiges Datum" };
    case z.ZodIssueCode.invalid_string: {
      const art = typeof issue.validation === "string" ? issue.validation : "regex";
      return { message: STRING_FORMAT[art] ?? "Ung\xFCltiges Format" };
    }
    case z.ZodIssueCode.too_small:
      if (issue.type === "string") {
        return {
          message: Number(issue.minimum) <= 1 ? "Darf nicht leer sein" : `Mindestens ${anzahl(issue.minimum, "Zeichen", "Zeichen")}`
        };
      }
      if (issue.type === "array" || issue.type === "set") {
        return {
          message: Number(issue.minimum) <= 1 ? "Mindestens ein Eintrag n\xF6tig" : `Mindestens ${anzahl(issue.minimum, "Eintrag", "Eintr\xE4ge")}`
        };
      }
      if (issue.type === "date") return { message: "Datum liegt zu fr\xFCh" };
      return {
        message: issue.inclusive ? `Muss mindestens ${zahl(issue.minimum)} sein` : `Muss gr\xF6\xDFer als ${zahl(issue.minimum)} sein`
      };
    case z.ZodIssueCode.too_big:
      if (issue.type === "string")
        return { message: `H\xF6chstens ${anzahl(issue.maximum, "Zeichen", "Zeichen")}` };
      if (issue.type === "array" || issue.type === "set") {
        return { message: `H\xF6chstens ${anzahl(issue.maximum, "Eintrag", "Eintr\xE4ge")}` };
      }
      if (issue.type === "date") return { message: "Datum liegt zu sp\xE4t" };
      return {
        message: issue.inclusive ? `Muss h\xF6chstens ${zahl(issue.maximum)} sein` : `Muss kleiner als ${zahl(issue.maximum)} sein`
      };
    case z.ZodIssueCode.not_multiple_of:
      return { message: `Muss ein Vielfaches von ${zahl(issue.multipleOf)} sein` };
    case z.ZodIssueCode.not_finite:
      return { message: "Muss eine endliche Zahl sein" };
    case z.ZodIssueCode.invalid_intersection_types:
      return { message: "Ung\xFCltige Eingabe" };
    case z.ZodIssueCode.custom:
      return { message: "Ung\xFCltige Eingabe" };
    default:
      return { message: "Ung\xFCltige Eingabe" };
  }
};
z.setErrorMap(deutscheFehlerKarte);

// src/validation/customer.schema.ts
import { z as z3 } from "zod";

// src/constants.ts
var COMPANY = {
  name: "avameo GmbH",
  email: "office@avameo.de",
  web: "www.avameo.de",
  ustId: "DE320535191",
  steuernr: "04022890897",
  kontakt: {
    name: "Sebastian Morgenbesser",
    email: "smorgenbesser@avameo.de",
    tel: "+491604092233"
  },
  banken: {
    commerzbank: {
      name: "Commerzbank",
      iban: "DE74 5104 0038 0717 3537 00",
      bic: "COBADEFFXXX"
    },
    nassauische: {
      name: "Nassauische Sparkasse",
      iban: "DE49 5105 0015 0107 1228 89",
      bic: "NASSDE55XXX"
    }
  }
};
var CI = {
  primary: "#00A6E5",
  secondary: "#90C73E",
  accents: ["#00A7C1", "#39B54A", "#00A89C"],
  dark: "#0F2A4A"
};
var INVOICE_NUMBER_REGEX = /^[A-Z0-9]{3}-\d{4}-\d{3,4}$/;
var INVOICE_NUMBER_LEGACY_REGEX = /^\d{3}-\d{4}-\d{4}$/;
var MWST_SAETZE = [0, 7, 19];
var STANDARD_MWST = 19;
var WAEHRUNGEN = ["EUR", "CHF", "USD", "GBP"];
var DEFAULT_WAEHRUNG = "EUR";
var DEFAULT_ZAHLUNGSZIEL = 30;
var MAHNSTUFEN = [
  { stufe: 1, tage: 7, gebuehr: 0 },
  { stufe: 2, tage: 14, gebuehr: 5 },
  { stufe: 3, tage: 30, gebuehr: 10 }
];
var FAELLIGKEITS_SCHWELLE_TAGE = 15;
var TAGE_PRO_MONAT = 20;
var STUNDEN_PRO_MONAT = 160;
var DEFAULT_PAGE_SIZE = 25;
var MAX_PAGE_SIZE = 100;
var RECURRING_INTERVALS = ["MONATLICH", "QUARTAL", "JAHR"];
var POSITION_KEY_MAP = {
  description: "beschreibung",
  qty: "menge",
  unit: "einheit",
  price: "einzelpreis",
  tax: "mwstSatz"
};
var UNIT_CODE_MAP = {
  HUR: "Stunden",
  DAY: "Tage",
  C62: "St\xFCck",
  LS: "Pauschal"
};
var VERMITTLER_UMSATZ_ERFASST_AB_JAHR = 2025;

// src/validation/invoice.schema.ts
import { z as z2 } from "zod";
var invoicePositionSchema = z2.object({
  beschreibung: z2.string().min(1).max(500),
  /** Sub-Detail, mehrzeilig moeglich. Wird im PDF unter beschreibung als
   *  Detailzeilen gerendert + im ZUGFeRD-XML als <ram:Description> separat
   *  zur <ram:Name> ausgegeben (statt — wie bisher — in beschreibung
   *  per " — " konkateniert). */
  detail: z2.string().max(500).optional().nullable(),
  /** BL-561: Leistungszeitraum JE POSITION (ISO-Datum). Vorher kannte das Schema die Felder nicht: Zod verwarf sie
   *  stillschweigend, obwohl das Formular sie schickte und die Spalten (`orderbuch_positions.leistung_von/_bis`) existierten.
   *  Ein ungueltiges Datumsformat wird jetzt abgelehnt statt verworfen. */
  leistungVon: z2.string().date().optional().nullable(),
  leistungBis: z2.string().date().optional().nullable(),
  menge: z2.number(),
  // kann auch negativ sein (Korrekturen/Gutschriften)
  einheit: z2.string().min(1).max(50),
  /**
   * BL-162 (24.08.2026): NICHT negativ — EN 16931 BR-27 verbietet einen
   * negativen Item-Netto-Preis. Zwei archivierte Rechnungen (841-0326-0997,
   * 856-0626-1019) verletzten das mit negativem `einzelpreis`, fielen bei
   * der Schematron-Prüfung durch. Für Korrekturen/Gutschriften stattdessen
   * `menge` negativ setzen (positiver Einzelpreis) — ergibt denselben
   * Rechnungsbetrag, ist aber EN-16931-konform.
   */
  einzelpreis: z2.number().min(
    0,
    "Einzelpreis darf nicht negativ sein (EN 16931 BR-27) \u2014 f\xFCr Korrekturen/Gutschriften stattdessen die Menge negativ setzen"
  ),
  mwstSatz: z2.number().refine((v) => MWST_SAETZE.includes(v), {
    message: `MwSt-Satz muss einer von ${MWST_SAETZE.join(", ")} sein`
  }),
  // Berechnete Felder — vom Server immer überschrieben.
  netto: z2.number().optional(),
  mwst: z2.number().optional()
});
function computePosition(p) {
  const netto = Math.round(p.menge * p.einzelpreis * 100) / 100;
  const mwst = Math.round(netto * p.mwstSatz / 100 * 100) / 100;
  return { ...p, netto, mwst };
}
function computePositions(positions) {
  return positions.map(computePosition);
}
function sumPositions(positions) {
  const netto = positions.reduce((s, p) => s + p.netto, 0);
  const mwst = positions.reduce((s, p) => s + p.mwst, 0);
  return {
    netto: Math.round(netto * 100) / 100,
    mwst: Math.round(mwst * 100) / 100,
    brutto: Math.round((netto + mwst) * 100) / 100
  };
}
var ATTACHMENT_MIME_TYPES = ["application/pdf", "image/png", "image/jpeg"];
var ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
var ATTACHMENT_MAX_TOTAL_BYTES = 20 * 1024 * 1024;
var ATTACHMENT_MAX_COUNT = 10;
var RECHNUNGSTEXT_MAX_LAENGE = 2e3;
var invoiceAttachmentSchema = z2.object({
  name: z2.string().min(1).max(255),
  description: z2.string().max(500).optional().nullable(),
  mimeType: z2.string().refine((v) => ATTACHMENT_MIME_TYPES.includes(v), {
    message: `MIME-Type muss einer von ${ATTACHMENT_MIME_TYPES.join(", ")} sein`
  }),
  size: z2.number().int().positive().max(ATTACHMENT_MAX_BYTES),
  /** Base64 ohne data:-Prefix. */
  data: z2.string().min(1)
});
var bankVerwendungSchema = z2.object({
  name: z2.string().min(1),
  iban: z2.string().min(15).max(34),
  bic: z2.string().min(8).max(11),
  inhaber: z2.string().optional()
});
var INVOICE_FORMATS = ["ZUGFERD", "XRECHNUNG"];
var invoiceFormatSchema = z2.enum(INVOICE_FORMATS);
var INVOICE_NUMBER_MANUAL_REGEX = /^\d{3}-(0[1-9]|1[0-2])\d{2}-[A-Za-z0-9_-]{3,12}$/;
var invoiceCreateSchema = z2.object({
  reNr: z2.string().regex(INVOICE_NUMBER_MANUAL_REGEX, "Format: NNN-MMJJ-KKKK (z.B. 851-0526-1022)").optional().nullable(),
  datum: z2.string().date(),
  kundeNr: z2.string().min(1),
  projektId: z2.number().int().positive().optional().nullable(),
  consultant: z2.string().max(200).optional().nullable(),
  consultantIds: z2.array(z2.number().int().positive()).optional().nullable(),
  // Empfänger-Snapshot
  empfaengerStrasse: z2.string().max(200).optional().nullable(),
  empfaengerAdresszusatz: z2.string().max(200).optional().nullable(),
  empfaengerPlz: z2.string().max(10).optional().nullable(),
  empfaengerOrt: z2.string().max(100).optional().nullable(),
  empfaengerLand: z2.string().length(2).optional().nullable(),
  empfaengerEmail: z2.string().email().optional().nullable().or(z2.literal("")),
  empfaengerUstId: z2.string().max(20).optional().nullable(),
  // Vertrags-Referenzen
  vertragsNr: z2.string().max(100).optional().nullable(),
  bestellNr: z2.string().max(100).optional().nullable(),
  kaeuferReferenz: z2.string().max(100).optional().nullable(),
  verwendungszweck: z2.string().max(500).optional().nullable(),
  bank: bankVerwendungSchema.optional().nullable(),
  // Leistungszeitraum
  lieferdatum: z2.string().date().optional().nullable(),
  zeitraumVon: z2.string().date().optional().nullable(),
  zeitraumBis: z2.string().date().optional().nullable(),
  // Zahlung
  zahlungsziel: z2.number().int().min(0).max(365).default(30),
  skonto: z2.number().min(0).max(100).default(0),
  /** Skonto-Frist in Tagen. NULL/undefined = globalen Default (settings) nutzen. */
  skontoTage: z2.number().int().min(0).max(365).optional().nullable(),
  // Inhalt
  positionen: z2.array(invoicePositionSchema).min(1, "Mindestens eine Position"),
  anlagen: z2.array(invoiceAttachmentSchema).max(ATTACHMENT_MAX_COUNT, `Maximal ${ATTACHMENT_MAX_COUNT} Anlagen pro Rechnung`).refine(
    (arr) => arr.reduce((s, a) => s + a.size, 0) <= ATTACHMENT_MAX_TOTAL_BYTES,
    `Anlagen insgesamt zu gro\xDF (Limit ${Math.round(ATTACHMENT_MAX_TOTAL_BYTES / 1024 / 1024)} MB)`
  ).optional().nullable(),
  notizen: z2.string().optional().nullable(),
  details: z2.string().optional().nullable(),
  /** BL-734: Text, der auf der Rechnung erscheint (anders als `details`, das nur intern ist). */
  rechnungstext: z2.string().max(RECHNUNGSTEXT_MAX_LAENGE).optional().nullable(),
  /** E-Rechnungs-Format. Wenn nicht gesetzt: Backend nutzt Customer-Default → ZUGFERD. */
  rechnungsFormat: invoiceFormatSchema.optional().nullable(),
  /** Rechnungswährung ISO 4217 (BL-086). Nicht gesetzt: Customer-Default → EUR.
   *  Alle Beträge der Rechnung sind in dieser Währung — keine Kursumrechnung. */
  waehrung: z2.enum(WAEHRUNGEN).optional().nullable()
});
var vorschauText = (max) => z2.string().max(max).nullish().catch(null);
var vorschauDatum = z2.string().date().nullish().catch(null);
var invoicePreviewSchema = z2.object({
  datum: vorschauDatum,
  kundeNr: z2.string().nullish().catch(null),
  projektId: z2.number().int().positive().nullish().catch(null),
  consultant: vorschauText(200),
  empfaengerStrasse: vorschauText(200),
  empfaengerAdresszusatz: vorschauText(200),
  empfaengerPlz: vorschauText(10),
  empfaengerOrt: vorschauText(100),
  empfaengerLand: vorschauText(2),
  empfaengerEmail: vorschauText(200),
  empfaengerUstId: vorschauText(20),
  vertragsNr: vorschauText(100),
  bestellNr: vorschauText(100),
  kaeuferReferenz: vorschauText(100),
  verwendungszweck: vorschauText(500),
  bank: bankVerwendungSchema.nullish().catch(null),
  lieferdatum: vorschauDatum,
  zeitraumVon: vorschauDatum,
  zeitraumBis: vorschauDatum,
  zahlungsziel: z2.number().int().min(0).max(365).catch(30),
  skonto: z2.number().min(0).max(100).catch(0),
  skontoTage: z2.number().int().min(0).max(365).nullish().catch(null),
  positionen: z2.array(
    z2.object({
      beschreibung: z2.string().max(500).catch(""),
      detail: z2.string().max(500).nullish().catch(null),
      leistungVon: vorschauDatum,
      leistungBis: vorschauDatum,
      menge: z2.number().catch(0),
      einheit: z2.string().max(50).catch(""),
      einzelpreis: z2.number().catch(0),
      mwstSatz: z2.number().catch(19)
    })
  ).max(200).catch([]),
  /** Nur Name und Beschreibung: die Vorschau braucht die Dateien nicht. */
  anlagen: z2.array(
    z2.object({
      name: z2.string().max(255).catch(""),
      description: z2.string().max(500).nullish().catch(null)
    })
  ).max(ATTACHMENT_MAX_COUNT).nullish().catch(null),
  notizen: z2.string().nullish().catch(null),
  details: z2.string().nullish().catch(null),
  rechnungstext: z2.string().max(RECHNUNGSTEXT_MAX_LAENGE).nullish().catch(null),
  rechnungsFormat: invoiceFormatSchema.nullish().catch(null),
  waehrung: z2.enum(WAEHRUNGEN).nullish().catch(null),
  /** Eine selbst vergebene Rechnungsnummer wird gezeigt, sonst steht der Platzhalter. */
  reNr: z2.string().regex(INVOICE_NUMBER_MANUAL_REGEX).nullish().catch(null)
});
var invoiceUpdateSchema = invoiceCreateSchema.omit({ reNr: true }).partial();
var invoiceStornoSchema = z2.object({
  stornoDatum: z2.string().date(),
  stornoGrund: z2.string().min(1).max(500),
  /**
   * BL-079 (2026-07-08): Wenn true, wird zusätzlich zur Storno-Markierung
   * eine Stornorechnung (Corrective-Beleg, CII TypeCode 384) mit negierten
   * Beträgen erzeugt und im GoBD-Archiv abgelegt. Empfänger, Positionen,
   * Consultant-Zuweisungen werden aus der Original-Rechnung übernommen und
   * negiert. Default true, weil der Buchhalter sonst kein Dokument zum
   * Nachbuchen bekommt — auf false setzen nur, wenn manuell gebucht wird.
   */
  erzeugeStornorechnung: z2.boolean().default(true)
});
var invoicePaymentSchema = z2.object({
  eingang: z2.string().date()
});

// src/validation/customer.schema.ts
var bankDetailsSchema = z3.object({
  name: z3.string().min(1),
  iban: z3.string().min(15).max(34),
  bic: z3.string().min(8).max(11),
  inhaber: z3.string().optional()
});
var customerCreateSchema = z3.object({
  nr: z3.string().min(1).max(20).regex(/^[A-Z0-9-]+$/),
  name: z3.string().min(1).max(200),
  strasse: z3.string().max(200).optional().nullable(),
  hausnummer: z3.string().max(30).optional().nullable(),
  adresszusatz: z3.string().max(200).optional().nullable(),
  plz: z3.string().max(10).optional().nullable(),
  ort: z3.string().max(100).optional().nullable(),
  land: z3.string().length(2).default("DE"),
  ustId: z3.string().trim().max(20).optional().nullable(),
  email: z3.string().email().optional().nullable().or(z3.literal("")),
  rechnungsEmail: z3.string().email().optional().nullable().or(z3.literal("")),
  kontaktName: z3.string().max(200).optional().nullable(),
  kontaktEmail: z3.string().email().optional().nullable().or(z3.literal("")),
  kontaktTel: z3.string().max(50).optional().nullable(),
  bank: bankDetailsSchema.optional().nullable(),
  notizen: z3.string().optional().nullable(),
  zahlungsziel: z3.number().int().min(0).max(365).default(30),
  skonto: z3.number().min(0).max(100).default(0),
  standardMwst: z3.number().min(0).max(100).default(19),
  buyerReference: z3.string().max(100).optional().nullable(),
  xrechnung: z3.boolean().default(false),
  /** Default-E-Rechnungs-Format. NULL = nimm globalen Default ZUGFERD. */
  rechnungsFormat: invoiceFormatSchema.optional().nullable(),
  einleitungstext: z3.string().optional().nullable(),
  /// DB-Audit P2/11 (04.05.2026): aliases ist jetzt nativer Postgres-TEXT[]-
  /// Array statt JSON-String. Frontend & Backend übergeben es direkt als
  /// String-Array. Default `[]` damit nullable-Handling überall entfällt.
  aliases: z3.array(z3.string().min(1).max(200)).max(20).default([]),
  /** Kunden-spezifisches Zahlungsziel (BL-071). NULL = globalen Default nutzen. */
  standardZahlungsziel: z3.number().int().min(0).max(365).optional().nullable(),
  /** Bevorzugte Bankverbindung. `bank1` = Commerzbank, `bank2` = Nassauische
   *  Sparkasse. NULL = keine Präferenz, User wählt manuell. */
  bevorzugteBank: z3.enum(["bank1", "bank2"]).optional().nullable(),
  /** Default-Währung für neue Rechnungen an diesen Kunden (BL-086).
   *  Nicht gesetzt = EUR. */
  waehrung: z3.enum(WAEHRUNGEN).optional().nullable(),
  /** Personalunion-Kennzeichnung (BL-228): Kunde ist zugleich Lieferant/
   *  Kreditor. Details dazu gehören ins bestehende `notizen`-Feld. */
  istAuchLieferant: z3.boolean().default(false)
});
var customerUpdateSchema = customerCreateSchema.partial().omit({ nr: true });

// src/validation/consultant.schema.ts
import { z as z4 } from "zod";
var optionalIsoDate = z4.union([
  z4.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum erwartet YYYY-MM-DD"),
  z4.literal(""),
  z4.null()
]).optional();
var consultantBaseSchema = z4.object({
  name: z4.string().min(1).max(200).optional(),
  vorname: z4.string().min(1).max(100).optional(),
  nachname: z4.string().min(1).max(100).optional(),
  /**
   * @deprecated BL-255 (02.09.2026) — Freitext-Rolle. Bleibt bis zum
   * Spalten-Drop akzeptiert, damit ein Client, der noch das alte Feld sendet,
   * nicht mit VALIDATION_ERROR abgewiesen wird. Neue Zuordnungen laufen
   * ueber `roleId`.
   */
  rolle: z4.string().max(100).optional().nullable(),
  tagessatz: z4.number().min(0).default(0),
  stundensatz: z4.number().min(0).default(0),
  email: z4.string().email().optional().nullable().or(z4.literal("")),
  status: z4.enum(["aktiv", "inaktiv", "extern"]).default("aktiv"),
  notizen: z4.string().optional().nullable(),
  // Personalcontrolling-Erweiterung (BL-036, 07.05.2026). Alle optional —
  // bestehende Consultants bleiben ohne diese Daten valide.
  personalNr: z4.string().max(20).optional().nullable(),
  level: z4.string().max(50).optional().nullable(),
  roleId: z4.number().int().positive().optional().nullable(),
  arbeitsstundenWoche: z4.number().min(0).max(80).optional().nullable(),
  urlaubstageJahr: z4.number().int().min(0).max(60).optional().nullable(),
  arbeitszeitProzent: z4.number().int().min(0).max(100).optional().nullable(),
  // Ist/Soll-Vergleich Personalcontrolling (Review 10.08.2026). Unabhängig
  // von arbeitszeitProzent (Teilzeit-Quote) — das ist der
  // betriebswirtschaftliche Zielwert für fakturierbare Auslastung.
  zielauslastungProzent: z4.number().int().min(0).max(100).optional().nullable(),
  eintrittAm: optionalIsoDate,
  austrittAm: optionalIsoDate,
  // BL-287 (07.09.2026): Start einer laufenden, durchgehenden Abwesenheit --
  // siehe Feld-Kommentar in schema.prisma / istLangzeitkrankAusgenommen.
  laufendeAbwesenheitSeit: optionalIsoDate,
  laufendeAbwesenheitArt: z4.string().max(100).optional().nullable()
});
var consultantCreateSchema = consultantBaseSchema.refine(
  (data) => Boolean(data.name) || Boolean(data.vorname && data.nachname),
  {
    message: "Entweder Name oder Vorname + Nachname erforderlich",
    path: ["vorname"]
  }
);
var consultantUpdateSchema = consultantBaseSchema.partial();
var roleCreateSchema = z4.object({
  name: z4.string().trim().min(1, "Bezeichnung erforderlich").max(100),
  aktiv: z4.boolean().default(true),
  zaehltInAuslastung: z4.boolean().default(true)
});
var roleUpdateSchema = roleCreateSchema.partial();

// src/validation/monthly-metric.schema.ts
import { z as z5 } from "zod";
var decimal4 = z5.number().min(0).max(40);
var decimal2 = z5.number().min(0).max(40);
var monthlyMetricUpsertSchema = z5.object({
  tagessatzSnapshot: z5.number().min(0).max(99999999).optional().nullable(),
  fakturierteTage: decimal2.optional(),
  umsatzEur: z5.number().min(0).max(9999999999e-2).optional(),
  urlaubstage: decimal4.optional(),
  krankheitstage: decimal4.optional(),
  schulungstage: decimal4.optional(),
  freigestellteTage: decimal4.optional(),
  // Plus = Ueberstunden, Minus = Unterstunden — Delta pro Monat (Q3).
  ueberstundenDelta: z5.number().min(-300).max(300).optional().nullable(),
  notiz: z5.string().max(500).optional().nullable()
});
var bulkAbsenceEntrySchema = z5.object({
  consultantId: z5.number().int().positive(),
  urlaubstage: decimal4.optional(),
  krankheitstage: decimal4.optional(),
  schulungstage: decimal4.optional(),
  freigestellteTage: decimal4.optional(),
  ueberstundenDelta: z5.number().min(-300).max(300).optional().nullable(),
  notiz: z5.string().max(500).optional().nullable()
});
var bulkAbsenceBodySchema = z5.object({
  jahr: z5.number().int().min(2e3).max(2099),
  monat: z5.number().int().min(1).max(12),
  entries: z5.array(bulkAbsenceEntrySchema).min(1).max(200)
});

// src/validation/project.schema.ts
import { z as z6 } from "zod";

// src/utils/project-status.ts
var PROJECT_STATUS = ["angebot", "gezeichnet", "aktiv", "pausiert", "beendet"];
var PROJECT_STATUS_LABEL = {
  angebot: "Angebot",
  gezeichnet: "Gezeichnet",
  aktiv: "Aktiv",
  pausiert: "Pausiert",
  beendet: "Beendet"
};
function projectStatusLabel(status) {
  return PROJECT_STATUS_LABEL[status];
}
var PROJECT_STATUS_NEU = "angebot";

// src/validation/project.schema.ts
var optionalIsoDate2 = z6.union([
  z6.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Datum erwartet YYYY-MM-DD"),
  z6.literal(""),
  z6.null()
]).optional();
var projectCreateSchema = z6.object({
  name: z6.string().min(1).max(300),
  kundeNr: z6.string().min(1),
  consultantId: z6.number().int().positive().optional().nullable(),
  tagessatz: z6.number().min(0).default(0),
  stundensatz: z6.number().min(0).default(0),
  vertragsart: z6.enum(["TuM", "Festpreis", "Wartung", "Support"]).default("TuM"),
  vertragsNr: z6.string().max(100).optional().nullable(),
  bestellNr: z6.string().max(100).optional().nullable(),
  kaeuferReferenz: z6.string().max(100).optional().nullable(),
  bank: z6.string().optional().nullable(),
  // JSON string
  laufzeitVon: optionalIsoDate2,
  laufzeitBis: optionalIsoDate2,
  status: z6.enum(PROJECT_STATUS).default(PROJECT_STATUS_NEU),
  notizen: z6.string().optional().nullable(),
  festpreis: z6.number().min(0).default(0),
  zahlungsziel: z6.number().int().min(0).max(365).default(30),
  skontoProzent: z6.number().min(0).max(100).default(0),
  skontoTage: z6.number().int().min(0).max(365).default(0),
  rechnungszyklus: z6.enum([
    "einmalig",
    "woechentlich",
    "halbmonatlich",
    "monatlich",
    "zweimonatlich",
    "quartal",
    "halbjaehrlich",
    "jaehrlich",
    "individuell"
  ]).default("monatlich"),
  taetigkeitsbeschreibung: z6.string().optional().nullable(),
  kontingentTyp: z6.enum(["keins", "tage", "stunden", "euro"]).default("keins"),
  kontingentGesamt: z6.number().min(0).default(0),
  /**
   * BL-593: VS1 Bodyleasing, VS2 Dienstleistungsportfolio, VS3 Produktgeschaeft.
   * BL-661: `KEIN` = ausserhalb der Geschaeftsbereiche (entschieden); das Warum steht in `notizen`.
   * BL-662 (23.10.2026, Entscheidung des Users: "Bei Projekten ist die Zuteilung zu einem Geschaeftsbereich ein Pflichtfeld"):
   * PFLICHT. Anlegen verlangt einen der vier Werte, `null` ("noch nicht zugeordnet") gibt es fuer neue Projekte nicht mehr; zumutbar
   * ist das nur wegen `KEIN`. Beim AENDERN erbt das Update-Schema ueber `.partial()` nur "darf fehlen" (Teil-Update, die Maske sendet
   * immer alle Felder), NICHT "darf null sein": ein ausdrueckliches Leeren wuerde die Pflicht fuer das Projekt wieder aufheben.
   * Der Bestand ist seit 23.10.2026 vollstaendig befuellt (Messung Prod-Deploy: 0 Projekte ohne Bereich), es gibt also keine Zeile,
   * die ueber die Maske nicht mehr speicherbar waere.
   */
  geschaeftsbereich: z6.enum(["VS1", "VS2", "VS3", "KEIN"])
});
var projectUpdateSchema = projectCreateSchema.partial();

// src/validation/auth.schema.ts
import { z as z7 } from "zod";
var loginSchema = z7.object({
  email: z7.string().email(),
  password: z7.string().min(1)
});
var registerSchema = z7.object({
  email: z7.string().email(),
  password: z7.string().min(12, "Passwort muss mindestens 12 Zeichen haben"),
  name: z7.string().min(1).max(200)
});
var changePasswordSchema = z7.object({
  oldPassword: z7.string().min(1),
  newPassword: z7.string().min(12)
}).refine((data) => data.oldPassword !== data.newPassword, {
  message: "Neues Passwort muss sich vom alten unterscheiden",
  path: ["newPassword"]
});
var totpCodeSchema = z7.string().regex(/^\d{6}$/, "6-stelliger Code erwartet");
var twoFaVerifySchema = z7.object({
  tempToken: z7.string().min(1),
  totpCode: totpCodeSchema
});
var twoFaConfirmSchema = z7.object({
  setupToken: z7.string().min(1),
  totpCode: totpCodeSchema
});
var twoFaDisableSchema = z7.object({
  totpCode: totpCodeSchema
});

// src/validation/gutschrift.schema.ts
import { z as z8 } from "zod";
var gutschriftPositionSchema = z8.object({
  beschreibung: z8.string().min(1).max(500),
  detail: z8.string().max(500).optional().nullable(),
  menge: z8.number(),
  einheit: z8.string().min(1).max(50),
  einzelpreis: z8.number(),
  mwstSatz: z8.number(),
  netto: z8.number()
});
var GUTSCHRIFT_BETRAGS_TOLERANZ = 0.05;
var GUTSCHRIFT_BETRAEGE_MELDUNG = `netto + mwst muss brutto ergeben (Toleranz ${GUTSCHRIFT_BETRAGS_TOLERANZ.toFixed(2).replace(".", ",")} EUR)`;
function gutschriftBetraegeWiderspruechlich(netto, mwst, brutto) {
  if (netto == null || mwst == null || brutto == null) return false;
  return Math.abs(netto + mwst - brutto) > GUTSCHRIFT_BETRAGS_TOLERANZ + 1e-9;
}
var gutschriftBasisSchema = z8.object({
  nr: z8.string().min(1).max(100),
  datum: z8.string().date(),
  netto: z8.number().min(0).optional().nullable(),
  mwst: z8.number().min(0).optional().nullable(),
  brutto: z8.number().min(0),
  aussteller: z8.string().max(200).optional().nullable(),
  consultant: z8.string().max(200).optional().nullable(),
  consultantId: z8.number().int().positive().optional().nullable(),
  projekt: z8.string().max(200).optional().nullable(),
  projektId: z8.number().int().positive().optional().nullable(),
  kundeNr: z8.string().max(20).optional().nullable(),
  zeitraum: z8.string().max(100).optional().nullable(),
  skonto: z8.number().min(0).max(100).optional().nullable(),
  eingang: z8.string().date().optional().nullable(),
  dateiname: z8.string().max(255).optional().nullable(),
  inOrderbuch: z8.boolean().default(false),
  positionen: z8.array(gutschriftPositionSchema).max(200).optional()
});
var gutschriftCreateSchema = gutschriftBasisSchema.superRefine((d, ctx) => {
  if (gutschriftBetraegeWiderspruechlich(d.netto, d.mwst, d.brutto)) {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["brutto"],
      message: GUTSCHRIFT_BETRAEGE_MELDUNG
    });
  }
});
var gutschriftUpdateSchema = gutschriftBasisSchema.partial().extend({
  gobdOverride: z8.object({ begruendung: z8.string() }).optional()
});

// src/validation/list-query.schema.ts
import { z as z9 } from "zod";
var listQuerySchema = z9.object({
  page: z9.coerce.number().int().min(1).default(1),
  limit: z9.coerce.number().int().min(1).max(200).default(50),
  search: z9.string().trim().max(200).optional().default("")
});

// src/validation/fibu/susa.schema.ts
import { z as z10 } from "zod";
var SUSA_QUELLEN = ["MANUAL", "CSV_KANZLEI", "PDF_ASSISTED"];
var SUSA_SECTION_TYPES = ["AUFWANDSKONTO", "KREDITOR", "SACHKONTO", "DEBITOR"];
var SUSA_PERIODEN_TYPEN = ["MONAT", "JAHR"];
var money = z10.number().min(-99999999999e-2).max(99999999999e-2);
var susaPositionInputSchema = z10.object({
  sectionType: z10.enum(SUSA_SECTION_TYPES),
  kontonummer: z10.string().trim().min(1).max(20),
  bezeichnung: z10.string().trim().min(1).max(200),
  ebWert: money.optional().nullable(),
  sollMonat: money.default(0),
  habenMonat: money.default(0),
  sollKumuliert: money.default(0),
  habenKumuliert: money.default(0),
  saldo: money
});
var susaImportCreateSchema = z10.object({
  jahr: z10.number().int().min(2e3).max(2099),
  /**
   * Bei `periodenTyp: 'MONAT'` der Buchungsmonat, bei `'JAHR'` der letzte vom
   * Master abgedeckte Monat (12 für ein abgeschlossenes Jahr, z.B. 6 für einen
   * Zwischenstand Jan–Jun).
   */
  monat: z10.number().int().min(1).max(12),
  /**
   * `MONAT` (Default) = klassische Monats-SuSa, Jahreswert entsteht als Summe
   * der Monate. `JAHR` = letzter Druck eines Jahres als Master, Jahreswert
   * kommt aus den kum.-Feldern. Default hält Alt-Clients und die
   * Erfassungsmaske unverändert gültig.
   */
  periodenTyp: z10.enum(SUSA_PERIODEN_TYPEN).default("MONAT"),
  /// Ausstellungsdatum des SuSa-PDFs laut Kanzlei (ISO-Datumsstring).
  standDatum: z10.string().refine((v) => !Number.isNaN(Date.parse(v)), "Ung\xFCltiges Datum"),
  quelle: z10.enum(SUSA_QUELLEN).default("MANUAL"),
  sourceLabel: z10.string().trim().max(200).optional().nullable(),
  notizen: z10.string().trim().max(1e3).optional().nullable(),
  positions: z10.array(susaPositionInputSchema).min(1).max(500)
});
var susaVoidSchema = z10.object({
  grund: z10.string().trim().min(3).max(500)
});

// src/validation/fibu/lobu.schema.ts
import { z as z11 } from "zod";
var LOBU_QUELLEN = ["MANUAL", "CSV_KANZLEI", "PDF_ASSISTED"];
var money2 = z11.number().min(-99999999999e-2).max(99999999999e-2);
var lobuPositionInputSchema = z11.object({
  personalNr: z11.string().trim().min(1).max(50),
  name: z11.string().trim().min(1).max(200),
  kostenstelle: z11.string().trim().max(50).optional().nullable(),
  beschaeftigungsart: z11.string().trim().max(50).optional().nullable(),
  eintrittsdatum: z11.string().refine((v) => !Number.isNaN(Date.parse(v)), "Ung\xFCltiges Datum").optional().nullable(),
  austrittsdatum: z11.string().refine((v) => !Number.isNaN(Date.parse(v)), "Ung\xFCltiges Datum").optional().nullable(),
  brutto: money2,
  einmalzahlung: money2.default(0),
  lohnsteuer: money2.default(0),
  svArbeitnehmer: money2.default(0),
  netto: money2,
  svArbeitgeber: money2.default(0),
  umlagen: money2.default(0),
  // BL-184: Erstattung BA (Kurzarbeitergeld u.ä.) / KK (U1/U2-Umlage) — mindert
  // die tatsächlichen Arbeitgeberkosten, siehe schema.prisma-Kommentar.
  erstattungBA: money2.default(0),
  erstattungKK: money2.default(0)
});
var lobuImportCreateSchema = z11.object({
  jahr: z11.number().int().min(2e3).max(2099),
  monat: z11.number().int().min(1).max(12),
  /// Ausstellungsdatum des Lohnjournals laut Kanzlei (ISO-Datumsstring).
  standDatum: z11.string().refine((v) => !Number.isNaN(Date.parse(v)), "Ung\xFCltiges Datum"),
  quelle: z11.enum(LOBU_QUELLEN).default("MANUAL"),
  sourceLabel: z11.string().trim().max(200).optional().nullable(),
  notizen: z11.string().trim().max(1e3).optional().nullable(),
  positions: z11.array(lobuPositionInputSchema).min(1).max(500),
  /**
   * BL-185: manche Imports lassen ein Jahr erstmals auf 12/12 importierte
   * LoBu-Monate springen — die Ergebnis-Rechnung (BL-143) wechselt dann
   * stillschweigend die Personalkosten-Quelle von SuSa auf LoBu, oft mit
   * spürbarem Sprung (realer Fall: ~50.000 € für 2024). Ohne dieses Flag
   * lehnt der Import einen solchen Wechsel mit 409 ab (Details in der
   * Fehlerantwort: SuSa-/LoBu-Wert, Differenz) — explizite Bestätigung
   * erforderlich, kein stiller Wechsel einer bereits veröffentlichten Kennzahl.
   */
  confirmPersonalkostenQuelle: z11.boolean().optional().default(false)
});
var lobuVoidSchema = z11.object({
  grund: z11.string().trim().min(3).max(500)
});

// src/validation/fibu/ustva.schema.ts
import { z as z12 } from "zod";
var USTVA_QUELLEN = ["MANUAL", "CSV_KANZLEI", "PDF_ASSISTED"];
var money3 = z12.number().min(-99999999999e-2).max(99999999999e-2);
var ustvaImportCreateSchema = z12.object({
  jahr: z12.number().int().min(2e3).max(2099),
  monat: z12.number().int().min(1).max(12),
  /// Datum der Voranmeldung/des Kanzlei-Ausdrucks (ISO-Datumsstring).
  standDatum: z12.string().refine((v) => !Number.isNaN(Date.parse(v)), "Ung\xFCltiges Datum"),
  quelle: z12.enum(USTVA_QUELLEN).default("MANUAL"),
  sourceLabel: z12.string().trim().max(200).optional().nullable(),
  notizen: z12.string().trim().max(1e3).optional().nullable(),
  /// Zeile 81 des Formulars "USt 1 A": steuerpflichtige Umsätze zu 19% (Bemessungsgrundlage netto).
  steuerpflichtigeUmsaetze19: money3,
  umsatzsteuer19: money3,
  vorsteuerbetraege: money3,
  /// Positiv = Zahllast, negativ = Überschuss/Erstattung.
  zahllastUeberschuss: money3
});
var ustvaVoidSchema = z12.object({
  grund: z12.string().trim().min(3).max(500)
});

// src/validation/fibu/opos.schema.ts
import { z as z13 } from "zod";
var OPOS_QUELLEN = ["MANUAL", "CSV_KANZLEI", "PDF_ASSISTED"];
var OPOS_SECTION_TYPES = ["DEBITOR", "KREDITOR"];
var money4 = z13.number().min(-99999999999e-2).max(99999999999e-2);
var isoDate = z13.string().refine((v) => !Number.isNaN(Date.parse(v)), "Ung\xFCltiges Datum").optional().nullable();
var oposPositionInputSchema = z13.object({
  section: z13.enum(OPOS_SECTION_TYPES),
  konto: z13.string().trim().min(1).max(20),
  beschriftung: z13.string().trim().min(1).max(200),
  rechnungsNr: z13.string().trim().max(50).optional().nullable(),
  datum: isoDate,
  faelligkeit: isoDate,
  gegenkonto: z13.string().trim().max(20).optional().nullable(),
  betragSoll: money4.default(0),
  betragHaben: money4.default(0),
  // Nullable, anders als bei SuSa — die Extraktion liefert den Saldo nur je
  // Konto, nicht für jeden Einzelposten (siehe schema.prisma-Kommentar).
  saldo: money4.optional().nullable(),
  faelligTage: z13.number().int().min(-99999).max(99999).optional().nullable(),
  buchungstext: z13.string().trim().max(500).optional().nullable()
});
var oposImportCreateSchema = z13.object({
  jahr: z13.number().int().min(2e3).max(2099),
  monat: z13.number().int().min(1).max(12),
  /// Stichtag der OPOS-Auswertung laut Kanzlei (ISO-Datumsstring).
  standDatum: z13.string().refine((v) => !Number.isNaN(Date.parse(v)), "Ung\xFCltiges Datum"),
  quelle: z13.enum(OPOS_QUELLEN).default("MANUAL"),
  sourceLabel: z13.string().trim().max(200).optional().nullable(),
  notizen: z13.string().trim().max(1e3).optional().nullable(),
  positions: z13.array(oposPositionInputSchema).min(1).max(5e3)
});
var oposVoidSchema = z13.object({
  grund: z13.string().trim().min(3).max(500)
});

// src/utils/adresse.ts
function strasseMitHausnummer(strasse, hausnummer) {
  const nummer = hausnummer?.trim() ?? "";
  if (nummer === "") return strasse ?? null;
  const rest = strasse?.trim() ?? "";
  return rest === "" ? nummer : `${rest} ${nummer}`;
}

// src/utils/eingang-datum.ts
function heuteInBerlin(jetzt = /* @__PURE__ */ new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin" }).format(jetzt);
}
function istEingangInZukunft(eingang, jetzt = /* @__PURE__ */ new Date()) {
  return eingang.slice(0, 10) > heuteInBerlin(jetzt);
}
var EINGANG_IN_ZUKUNFT_MELDUNG = "Ein Zahlungseingang kann nicht in der Zukunft liegen. Bitte das Datum des tats\xE4chlichen Eingangs eintragen (heute oder fr\xFCher).";

// src/utils/rechnungs-email.ts
function rechnungsEmailVon(kunde) {
  const eigene = kunde?.rechnungsEmail?.trim() ?? "";
  if (eigene !== "") return eigene;
  return kunde?.email ?? null;
}

// src/utils/rechnungstext.ts
function bereinigeRechnungstext(text) {
  if (text == null) return null;
  const s = text.replace(/\r\n|\r|\u0085|\u2028|\u2029/g, "\n").replace(/\t/g, " ").replace(/[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/g, "").replace(/[\u202A-\u202E\u2066-\u2069\uFEFF\uFFFE\uFFFF\uFFF9-\uFFFB]/g, "").replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "").replace(/[ ]+\n/g, "\n").trim();
  return s === "" ? null : s;
}

// src/utils/auftragswert.ts
var AUFTRAGSWERT_QUELLEN = ["KONTINGENT_EURO", "PROJEKTVOLUMEN"];
var istGepflegt = (v) => typeof v === "number" && Number.isFinite(v) && v > 0;
var euro = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function bestimmeAuftragswert(projekt) {
  const { kontingentTyp, kontingentGesamt, festpreis } = projekt;
  if (kontingentTyp === "euro" && istGepflegt(kontingentGesamt)) {
    const abweichend = istGepflegt(festpreis) && festpreis !== kontingentGesamt;
    return {
      auftragswert: kontingentGesamt,
      auftragswertQuelle: "KONTINGENT_EURO",
      auftragswertHinweis: abweichend ? `Kontingent (${euro(kontingentGesamt)} EUR) und Projektvolumen (${euro(festpreis)} EUR) weichen ab; ausgewiesen wird das Kontingent, nicht umgerechnet.` : null
    };
  }
  const einheit = kontingentTyp === "tage" ? "Tagen" : kontingentTyp === "stunden" ? "Stunden" : null;
  const hinweis = einheit !== null && istGepflegt(kontingentGesamt) ? `Das Kontingent ist in ${einheit} angegeben und kein Euro-Betrag${istGepflegt(festpreis) ? "; ausgewiesen wird das Projektvolumen." : "; ein Projektvolumen ist nicht gepflegt."}` : null;
  if (istGepflegt(festpreis)) {
    return {
      auftragswert: festpreis,
      auftragswertQuelle: "PROJEKTVOLUMEN",
      auftragswertHinweis: hinweis
    };
  }
  return { auftragswert: null, auftragswertQuelle: null, auftragswertHinweis: hinweis };
}

// src/utils/auslastung.ts
function getArbeitstageImMonat(jahr, monat) {
  const tageImMonat = new Date(Date.UTC(jahr, monat, 0)).getUTCDate();
  let arbeitstage = 0;
  for (let tag = 1; tag <= tageImMonat; tag++) {
    const wochentag = new Date(Date.UTC(jahr, monat - 1, tag)).getUTCDay();
    if (wochentag !== 0 && wochentag !== 6) arbeitstage++;
  }
  return arbeitstage;
}
function berechneAuslastungFaktor(input) {
  const arbeitstageImMonat = getArbeitstageImMonat(input.jahr, input.monat);
  const vertragsFaktor = (input.arbeitszeitProzent ?? 100) / 100;
  const verfuegbareTage = arbeitstageImMonat * vertragsFaktor - input.urlaubstage - input.krankheitstage - input.schulungstage - input.freigestellteTage;
  if (verfuegbareTage <= 0) return 0;
  return Math.round(input.fakturierteTage / verfuegbareTage * 100) / 100;
}
var LANGZEITKRANK_SCHWELLE_TAGE = 42;
function istLangzeitkrankAusgenommen(laufendeAbwesenheitSeit, heute = /* @__PURE__ */ new Date(), schwellenTage = LANGZEITKRANK_SCHWELLE_TAGE) {
  if (!laufendeAbwesenheitSeit) return false;
  const tageSeitBeginn = Math.floor(
    (heute.getTime() - laufendeAbwesenheitSeit.getTime()) / 864e5
  );
  return tageSeitBeginn >= schwellenTage;
}

// src/utils/format.ts
var LOCALE_DE = "de-DE";
var CURRENCY_EUR = "EUR";
var TIMEZONE_BERLIN = "Europe/Berlin";
function formatBerlinDateTime(date) {
  return date.toLocaleString(LOCALE_DE, { timeZone: TIMEZONE_BERLIN, timeZoneName: "short" });
}
function formatProzent(wert, digits = 1) {
  if (typeof wert !== "number" || !Number.isFinite(wert)) return "\u2013";
  const zahl2 = wert.toLocaleString(LOCALE_DE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
  return `${zahl2}\xA0%`;
}
function escapeXml(s) {
  if (s === null || s === void 0) return "";
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}
function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

// src/utils/billing-periods.ts
function monthsPerCycle(z14) {
  switch (z14) {
    case "einmalig":
      return 1;
    // Einmalig wird oft als Festpreis gefuehrt; sonst 1 Monat als pragmatischer Default
    case "woechentlich":
      return 0.25;
    // grob 1/4 Monat
    case "halbmonatlich":
      return 0.5;
    case "monatlich":
      return 1;
    case "zweimonatlich":
      return 2;
    case "quartal":
      return 3;
    case "halbjaehrlich":
      return 6;
    case "jaehrlich":
      return 12;
    case "individuell":
      return 0;
  }
}
function formatDate(d) {
  return d.toISOString().slice(0, 10);
}
function pushMonthlyRange(periods, year, startMonth, monthsCount, laufzeitVon, laufzeitBis, labelFn, colSpan) {
  for (let m = startMonth; m < 12; m += monthsCount) {
    const von = new Date(Date.UTC(year, m, 1));
    const bis = new Date(Date.UTC(year, m + monthsCount, 0));
    if (laufzeitVon && bis < laufzeitVon) continue;
    if (laufzeitBis && von > laufzeitBis) continue;
    periods.push({
      von: formatDate(von),
      bis: formatDate(bis),
      label: labelFn(von, bis),
      colSpan
    });
  }
}
function getBillingPeriodsForProject(project, year) {
  const periods = [];
  const z14 = project.rechnungszyklus;
  if (z14 === "individuell") return periods;
  const laufzeitVon = project.laufzeitVon ? new Date(project.laufzeitVon) : null;
  const laufzeitBis = project.laufzeitBis ? new Date(project.laufzeitBis) : null;
  if (z14 === "einmalig") {
    const yearStart = new Date(Date.UTC(year, 0, 1));
    const yearEnd = new Date(Date.UTC(year, 11, 31));
    const von = laufzeitVon && laufzeitVon > yearStart ? laufzeitVon : yearStart;
    const bis = laufzeitBis && laufzeitBis < yearEnd ? laufzeitBis : yearEnd;
    if (laufzeitVon && yearEnd < laufzeitVon) return periods;
    if (laufzeitBis && yearStart > laufzeitBis) return periods;
    periods.push({
      von: formatDate(von),
      bis: formatDate(bis),
      label: `Einmalig ${year}`,
      colSpan: 12
    });
    return periods;
  }
  if (z14 === "jaehrlich") {
    const von = new Date(Date.UTC(year, 0, 1));
    const bis = new Date(Date.UTC(year, 11, 31));
    if (laufzeitVon && bis < laufzeitVon) return periods;
    if (laufzeitBis && von > laufzeitBis) return periods;
    periods.push({
      von: formatDate(von),
      bis: formatDate(bis),
      label: `Jahr ${year}`,
      colSpan: 12
    });
    return periods;
  }
  if (z14 === "halbjaehrlich") {
    pushMonthlyRange(
      periods,
      year,
      0,
      6,
      laufzeitVon,
      laufzeitBis,
      (von) => `${von.getMonth() === 0 ? "H1" : "H2"} ${year}`,
      6
    );
    return periods;
  }
  if (z14 === "quartal") {
    pushMonthlyRange(
      periods,
      year,
      0,
      3,
      laufzeitVon,
      laufzeitBis,
      (von) => `Q${Math.floor(von.getMonth() / 3) + 1} ${year}`,
      3
    );
    return periods;
  }
  if (z14 === "zweimonatlich") {
    pushMonthlyRange(
      periods,
      year,
      0,
      2,
      laufzeitVon,
      laufzeitBis,
      (von, bis) => `${von.toLocaleDateString(LOCALE_DE, { month: "short" })}\u2013${bis.toLocaleDateString(LOCALE_DE, { month: "short" })} ${year}`,
      2
    );
    return periods;
  }
  if (z14 === "monatlich") {
    pushMonthlyRange(
      periods,
      year,
      0,
      1,
      laufzeitVon,
      laufzeitBis,
      (von) => von.toLocaleDateString(LOCALE_DE, { month: "short", year: "numeric" }),
      1
    );
    return periods;
  }
  if (z14 === "halbmonatlich") {
    for (let m = 0; m < 12; m++) {
      const v1 = new Date(Date.UTC(year, m, 1));
      const b1 = new Date(Date.UTC(year, m, 15));
      const v2 = new Date(Date.UTC(year, m, 16));
      const b2 = new Date(Date.UTC(year, m + 1, 0));
      const monthLabel = v1.toLocaleDateString(LOCALE_DE, { month: "short", timeZone: "UTC" });
      for (const [von, bis, half] of [
        [v1, b1, "H1"],
        [v2, b2, "H2"]
      ]) {
        if (laufzeitVon && bis < laufzeitVon) continue;
        if (laufzeitBis && von > laufzeitBis) continue;
        periods.push({
          von: formatDate(von),
          bis: formatDate(bis),
          label: `${monthLabel} ${half}`,
          colSpan: 1
          // Kalender-Spalte = Monat; bei halbmonatlich passen 2 in einen Monat
        });
      }
    }
    return periods;
  }
  if (z14 === "woechentlich") {
    const start = new Date(Date.UTC(year, 0, 1));
    const end = new Date(Date.UTC(year, 11, 31));
    let weekStart = start;
    let weekIdx = 1;
    while (weekStart <= end) {
      const dayOfWeek = weekStart.getDay();
      const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + daysUntilSunday);
      const clamped = weekEnd > end ? end : weekEnd;
      if ((!laufzeitVon || clamped >= laufzeitVon) && (!laufzeitBis || weekStart <= laufzeitBis)) {
        periods.push({
          von: formatDate(weekStart),
          bis: formatDate(clamped),
          label: `KW ${weekIdx} ${year}`,
          colSpan: 1
        });
      }
      const next = new Date(clamped);
      next.setDate(next.getDate() + 1);
      weekStart = next;
      weekIdx += 1;
    }
    return periods;
  }
  return periods;
}
function getPeriodStatus(periodBis, hasBilledInvoice, now = /* @__PURE__ */ new Date()) {
  if (hasBilledInvoice) return "billed";
  const bisDate = new Date(periodBis);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const periodEnd = new Date(bisDate.getFullYear(), bisDate.getMonth(), bisDate.getDate());
  if (periodEnd > today) {
    return periodEnd.getMonth() === today.getMonth() && periodEnd.getFullYear() === today.getFullYear() ? "running" : "upcoming";
  }
  const diffDays = Math.floor((today.getTime() - periodEnd.getTime()) / (1e3 * 60 * 60 * 24));
  return diffDays > FAELLIGKEITS_SCHWELLE_TAGE ? "overdue" : "due";
}
function estimateProjectPeriodNetto(project) {
  if (project.rechnungszyklus === "individuell") return 0;
  const multiplier = monthsPerCycle(project.rechnungszyklus);
  if (project.vertragsart === "Festpreis" && project.festpreis > 0) {
    return project.festpreis;
  }
  if (project.tagessatz > 0) return project.tagessatz * TAGE_PRO_MONAT * multiplier;
  if (project.stundensatz > 0) return project.stundensatz * STUNDEN_PRO_MONAT * multiplier;
  return 0;
}

// src/utils/bwa-categories.ts
var BWA_KONTEN_MAP = {
  "2118": "ZINSAUFWAND",
  // Zinsen auf Kontokorrentkonten
  "2120": "ZINSAUFWAND",
  // Zinsaufwendungen f. langfristige Verbindlichkeiten
  "2141": "ZINSAUFWAND",
  // Kreditprovision, Verwaltungskostenbeitrag (Bankgebühr, kein Verwaltungsaufwand)
  "2100": "ZINSAUFWAND",
  // Zinsen und ähnliche Aufwendungen
  "2110": "ZINSAUFWAND",
  // Zinsaufwendungen f. kurzfristige Verbindlichkeiten
  "2650": "ZINSERTRAG",
  // Sonstige Zinsen und ähnliche Erträge
  "2020": "NEUTRALER_AUFWAND",
  // Periodenfremde Aufwendungen
  "2382": "NEUTRALER_AUFWAND",
  // Zuwendungen, Spenden mildtätige Zwecke
  "2381": "NEUTRALER_AUFWAND",
  // Zuwendungen, Spenden wissenschaftl./kulturelle Zwecke (analog 2382)
  "2309": "NEUTRALER_AUFWAND",
  // Sonstige Aufwendungen unregelmäßig (Aufwand-Gegenstück zu 2709)
  "2520": "NEUTRALER_ERTRAG",
  // Periodenfremde Erträge
  "2709": "NEUTRALER_ERTRAG",
  // Sonstige Erträge unregelmäßig
  "2742": "NEUTRALER_ERTRAG",
  // Versicherungsentschädigung, Schadenersatz
  "2660": "NEUTRALER_ERTRAG",
  // Erträge aus der Währungsumrechnung
  "2735": "NEUTRALER_ERTRAG",
  // Erträge aus der Auflösung von Rückstellungen
  "2743": "NEUTRALER_ERTRAG",
  // Investitionszuschüsse
  // BL-231 (28.08.2026): Kanzlei-Klärung eingetroffen, siehe Doc-Comment oben.
  "2749": "NEUTRALER_ERTRAG",
  // Erstattungen Aufwendungsausgleichsgesetz
  // BL-231-Korrektur (28.08.2026): war zunächst 'SONSTIGE_BETRIEBLICHE_ERLOESE'
  // (BL-221, der Anforderungstext Abschnitt 2 legte das nahe) -- der echte
  // DATEV-Wertenachweis der Kanzlei (Februar 2026, SKR03, BWA-Nr. 1) ordnet
  // 2752 dem neutralen Ergebnis zu, nicht den sonstigen betrieblichen
  // Erlösen. Wirkt sich NUR auf die Betriebsergebnis/neutrales-Ergebnis-
  // Aufteilung aus, nicht auf `ergebnisVorSteuern` (beide Positionen liegen
  // oberhalb davon und werden gleichermaßen aufsummiert).
  "2752": "NEUTRALER_ERTRAG"
  // Erlöse Vermietung u. Verpachtung 19% USt
};
function klassifiziereSachkonto(kontonummer) {
  const n = Number.parseInt(kontonummer.trim(), 10);
  if (!Number.isFinite(n) || n < 2e3 || n > 2999) return { art: "BILANZ" };
  const position = BWA_KONTEN_MAP[kontonummer.trim()];
  return position ? { art: "BEKANNT", position } : { art: "UNBEKANNT" };
}

// src/utils/company-identifiers.ts
var COMPANY_IDENTIFIERS = [
  {
    key: "companyBetriebsnummer",
    label: "Betriebsnummer (Bundesagentur f\xFCr Arbeit)",
    hint: "8 Ziffern",
    placeholder: "12345678",
    pattern: /^\d{8}$/
  },
  {
    key: "companyDuns",
    label: "DUNS-Nummer",
    hint: "9 Ziffern",
    placeholder: "123456789",
    pattern: /^\d{9}$/
  }
];
function pruefeFirmenKennung(key, value) {
  const kennung = COMPANY_IDENTIFIERS.find((k) => k.key === key);
  if (!kennung) return null;
  const wert = value.trim();
  if (wert === "") return null;
  return kennung.pattern.test(wert) ? null : `${kennung.label}: ung\xFCltiges Format (${kennung.hint}, nur Ziffern)`;
}

// src/utils/expense-categories.ts
var EXPENSE_KATEGORIE_MAP = [
  { von: 4100, bis: 4199, kategorie: "Personal" },
  { von: 4200, bis: 4299, kategorie: "Raum" },
  { von: 4300, bis: 4399, kategorie: "Versicherungen & Beitr\xE4ge" },
  { von: 4500, bis: 4599, kategorie: "Fahrzeug" },
  { von: 4600, bis: 4659, kategorie: "Werbung & Repr\xE4sentation" },
  { von: 4660, bis: 4699, kategorie: "Reisekosten" },
  { von: 4800, bis: 4899, kategorie: "Abschreibungen" },
  { von: 4940, bis: 4949, kategorie: "Fortbildung & Personal-Nebenkosten" },
  { von: 4950, bis: 4959, kategorie: "Rechts- & Beratungskosten" },
  { von: 4960, bis: 4969, kategorie: "IT & Lizenzen" },
  { von: 4900, bis: 4939, kategorie: "Verwaltung & Kommunikation" },
  { von: 4970, bis: 4999, kategorie: "Sonstige betriebliche Aufwendungen" }
];
var EXPENSE_KATEGORIE_SONSTIGES = "Sonstiges";
function categorizeKonto(kontonummer) {
  const n = parseInt(kontonummer, 10);
  if (!Number.isFinite(n)) return EXPENSE_KATEGORIE_SONSTIGES;
  for (const range of EXPENSE_KATEGORIE_MAP) {
    if (n >= range.von && n <= range.bis) return range.kategorie;
  }
  return EXPENSE_KATEGORIE_SONSTIGES;
}

// src/utils/ergebnisrechnung-categories.ts
var UMSATZERLOESE_KONTEN = ["8400", "8519", "8736", "8338", "8410", "8735"];
var SONSTIGE_BETRIEBLICHE_ERLOESE_KONTEN = ["8610", "8611"];
var UMSATZERLOESE_KONTEN_SET = new Set(UMSATZERLOESE_KONTEN);
var SONSTIGE_BETRIEBLICHE_ERLOESE_KONTEN_SET = new Set(
  SONSTIGE_BETRIEBLICHE_ERLOESE_KONTEN
);
function categorizeErloesKonto(kontonummer) {
  if (UMSATZERLOESE_KONTEN_SET.has(kontonummer)) return "UMSATZERLOESE";
  if (SONSTIGE_BETRIEBLICHE_ERLOESE_KONTEN_SET.has(kontonummer)) {
    return "SONSTIGE_BETRIEBLICHE_ERLOESE";
  }
  return "ERLOESE_UNKLASSIFIZIERT";
}
function isMaterialKonto(kontonummer) {
  const n = Number.parseInt(kontonummer.trim(), 10);
  return Number.isFinite(n) && n >= 3e3 && n <= 3999;
}
function isErloesKonto(kontonummer) {
  const n = Number.parseInt(kontonummer.trim(), 10);
  return Number.isFinite(n) && n >= 8e3 && n <= 8999;
}

// src/utils/geschaeftsbereich.ts
var GESCHAEFTSBEREICHE = ["VS1", "VS2", "VS3"];
var GESCHAEFTSBEREICH_LABEL = {
  VS1: "VS1 Bodyleasing",
  VS2: "VS2 Dienstleistungsportfolio",
  VS3: "VS3 Produktgesch\xE4ft"
};
var GESCHAEFTSBEREICH_KEIN = "KEIN";
var GESCHAEFTSBEREICH_KEIN_LABEL = "Au\xDFerhalb der Gesch\xE4ftsbereiche";
var GESCHAEFTSBEREICH_KEIN_HINWEIS = "\u201EAu\xDFerhalb der Gesch\xE4ftsbereiche\u201C ist eine Entscheidung (z. B. F\xF6rderung oder Nebengesch\xE4ft), keiner der drei Bereiche. Den Grund bitte in den Notizen festhalten.";
function geschaeftsbereichLabel(bereich) {
  return GESCHAEFTSBEREICH_LABEL[bereich];
}

// src/utils/project-status-regel.ts
function tagVon(d) {
  if (!d) return null;
  return typeof d === "string" ? d.slice(0, 10) : d.toISOString().slice(0, 10);
}
function heuteTag(heute) {
  const m = String(heute.getMonth() + 1).padStart(2, "0");
  const t = String(heute.getDate()).padStart(2, "0");
  return `${heute.getFullYear()}-${m}-${t}`;
}
function berechneterStatus(laufzeitVon, laufzeitBis, heute) {
  const h = heuteTag(heute);
  const bis = tagVon(laufzeitBis);
  const von = tagVon(laufzeitVon);
  if (bis !== null && bis <= h) return "beendet";
  if (von !== null && von > h) return null;
  return "aktiv";
}
function statusIstManuell(status, laufzeitVon, laufzeitBis, heute) {
  if (status !== "aktiv" && status !== "beendet") return false;
  return status !== berechneterStatus(laufzeitVon, laufzeitBis, heute);
}
function gezeichnetWirdAktiv(status, laufzeitVon, laufzeitBis, heute) {
  if (status !== "gezeichnet") return false;
  const von = tagVon(laufzeitVon);
  if (von === null) return false;
  return berechneterStatus(von, laufzeitBis, heute) === "aktiv";
}
function projektStatusWarnung(status, laufzeitVon, laufzeitBis, heute) {
  if (status !== "angebot" && status !== "gezeichnet") return null;
  const h = heuteTag(heute);
  const von = tagVon(laufzeitVon);
  const bis = tagVon(laufzeitBis);
  if (bis !== null && bis <= h) return "Laufzeit abgelaufen, Status nie auf aktiv gestellt";
  if (status === "angebot" && von !== null && von <= h)
    return "Laufzeit hat begonnen, Angebot nicht best\xE4tigt";
  return null;
}

// src/utils/invoice-consistency.ts
var TOLERANCE = 0.02;
var KNOWN_RATES = [0, 7, 16, 19];
var MWST_PLAUSIBLE_MAX_RATIO = 0.25;
function checkConsistency(input) {
  const { netto, mwst, betrag, storniert } = input;
  if (netto === null || mwst === null) {
    return {
      status: "unvollst\xE4ndig",
      diff: 0,
      implizerSatzProzent: null,
      erklaerung: "netto oder mwst ist null \u2014 nicht pr\xFCfbar"
    };
  }
  const impliziterSatz = netto > 0 ? roundTo((betrag - netto) / netto * 100, 1) : null;
  const diff = roundTo(betrag - (netto + mwst), 2);
  if (storniert && betrag <= 0 && Math.abs(netto) < TOLERANCE && Math.abs(mwst) < TOLERANCE) {
    return {
      status: "storno",
      diff,
      implizerSatzProzent: impliziterSatz,
      erklaerung: "Storno-Muster (Legacy-DB): netto=mwst=0, betrag negativ"
    };
  }
  if (netto > TOLERANCE && mwst > netto * MWST_PLAUSIBLE_MAX_RATIO) {
    return {
      status: "mwst_zu_hoch",
      diff,
      implizerSatzProzent: impliziterSatz,
      erklaerung: `MwSt(${mwst}) > 25% vom Netto(${netto}) \u2014 physikalisch unm\xF6glich (max. realer Satz 19%). Vermuteter Vertausch netto\u2194mwst. Fix: netto und mwst vertauschen, dann erneut pr\xFCfen.`
    };
  }
  if (Math.abs(diff) <= TOLERANCE) {
    return {
      status: "ok",
      diff,
      implizerSatzProzent: impliziterSatz,
      erklaerung: "netto + mwst = betrag (konsistent)"
    };
  }
  if (Math.abs(betrag - netto) < TOLERANCE && mwst > TOLERANCE) {
    return {
      status: "brutto_fehlt",
      diff,
      implizerSatzProzent: impliziterSatz,
      erklaerung: "betrag = netto, mwst > 0 \u2014 Brutto fehlt (Fix: betrag = netto + mwst)"
    };
  }
  if (betrag === 0 && (netto > 0 || mwst > 0)) {
    return {
      status: "betrag_0",
      diff,
      implizerSatzProzent: impliziterSatz,
      erklaerung: "betrag = 0 bei netto/mwst > 0 \u2014 vermutlich Entwurf / fehlerhaft angelegt"
    };
  }
  const BUG_THRESHOLD = 1;
  const mwstSollwert = betrag - netto;
  const mwstAbweichung = Math.abs(mwst - mwstSollwert);
  if (impliziterSatz !== null && KNOWN_RATES.some((r) => Math.abs(r - impliziterSatz) < 0.5) && mwstAbweichung > BUG_THRESHOLD) {
    return {
      status: "mwst_ist_satz",
      diff,
      implizerSatzProzent: impliziterSatz,
      erklaerung: `mwst-Spalte (${mwst}) ist vermutlich fehlerhaft. Impliziter Satz aus (brutto-netto)/netto: ${impliziterSatz}% \u2014 Fix: mwst = betrag - netto = ${roundTo(mwstSollwert, 2)}`
    };
  }
  return {
    status: "inkonsistent",
    diff,
    implizerSatzProzent: impliziterSatz,
    erklaerung: `netto(${netto}) + mwst(${mwst}) = ${netto + mwst}, aber betrag = ${betrag} (diff ${diff})`
  };
}
function isConsistent(input) {
  const r = checkConsistency(input);
  return r.status === "ok" || r.status === "storno";
}
function suggestMwstFix(input) {
  const r = checkConsistency(input);
  if (r.status !== "mwst_ist_satz") return null;
  if (input.netto === null) return null;
  return roundTo(input.betrag - input.netto, 2);
}
function suggestBruttoFix(input) {
  const r = checkConsistency(input);
  if (r.status !== "brutto_fehlt") return null;
  if (input.netto === null || input.mwst === null) return null;
  return roundTo(input.netto + input.mwst, 2);
}
function roundTo(n, decimals) {
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

// src/utils/period-shift.ts
var ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
function parse(iso) {
  const m = ISO.exec(iso.length > 10 && iso[10] === "T" ? iso.slice(0, 10) : iso);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > daysInMonth(y, mo)) return null;
  return { y, m: mo, d };
}
function format({ y, m, d }) {
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function daysInMonth(year, month) {
  if (month === 2) return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0 ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}
function addMonths({ y, m, d }, n) {
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = total % 12 + 1;
  return { y: ny, m: nm, d: Math.min(d, daysInMonth(ny, nm)) };
}
function verschiebeDatumUmEinenMonat(iso) {
  if (!iso) return null;
  const p = parse(iso);
  if (!p) return null;
  return format(addMonths(p, 1));
}
function verschiebeZeitraumFuerFolgerechnung(von, bis) {
  const pv = von ? parse(von) : null;
  const pb = bis ? parse(bis) : null;
  if (pv && pb && pv.d === 1 && pb.d === daysInMonth(pb.y, pb.m)) {
    const n = (pb.y - pv.y) * 12 + (pb.m - pv.m) + 1;
    if (n >= 1) {
      const start = addMonths(pv, n);
      const ende = addMonths({ ...pv, d: 1 }, 2 * n - 1);
      return {
        von: format({ y: start.y, m: start.m, d: 1 }),
        bis: format({ y: ende.y, m: ende.m, d: daysInMonth(ende.y, ende.m) })
      };
    }
  }
  return { von: verschiebeDatumUmEinenMonat(von), bis: verschiebeDatumUmEinenMonat(bis) };
}
function verschiebePositionsZeitraum(von, bis) {
  const z14 = verschiebeZeitraumFuerFolgerechnung(von, bis);
  return { leistungVon: z14.von ?? void 0, leistungBis: z14.bis ?? void 0 };
}
var FolgeVerschiebung = {
  datum: verschiebeDatumUmEinenMonat,
  zeitraum: verschiebeZeitraumFuerFolgerechnung,
  position: verschiebePositionsZeitraum
};

// src/utils/invoice-number.ts
function getKundenKuerzel(kundenName) {
  const cleaned = kundenName.replace(/GmbH|AG|KG|OHG|UG|e\.V\.|Inc\.|Ltd\.|LLC|Co\./gi, "").trim().toUpperCase().replace(/[^A-ZÄÖÜ]/g, "");
  const normalized = cleaned.replace(/Ä/g, "AE").replace(/Ö/g, "OE").replace(/Ü/g, "UE");
  return normalized.slice(0, 3).padEnd(3, "X");
}
function formatInvoiceNumber(kuerzel, month, year, laufnummer) {
  const mm = String(month).padStart(2, "0");
  const jj = String(year).slice(-2);
  const nnnn = String(laufnummer).padStart(4, "0");
  return `${kuerzel}-${mm}${jj}-${nnnn}`;
}
function parseInvoiceNumber(reNr) {
  if (!INVOICE_NUMBER_REGEX.test(reNr)) return null;
  const parts = reNr.split("-");
  if (parts.length !== 3) return null;
  const [kuerzel, mmjj, nnnn] = parts;
  return {
    kuerzel,
    month: parseInt(mmjj.slice(0, 2), 10),
    year: 2e3 + parseInt(mmjj.slice(2, 4), 10),
    laufnummer: parseInt(nnnn, 10)
  };
}
function isValidInvoiceNumber(reNr) {
  return INVOICE_NUMBER_REGEX.test(reNr);
}
function isSafeInvoiceNumberParam(s) {
  if (!s || s.length > 64) return false;
  return /^[A-Za-z0-9\- ]+$/.test(s);
}
function assertSafeInvoiceNumberParam(s) {
  if (!isSafeInvoiceNumberParam(s)) {
    throw new Error(`Ung\xFCltiges Rechnungsnummer-Format: ${s.slice(0, 30)}`);
  }
  return s;
}
function matchesAnyInvoiceFormat(reNr) {
  return INVOICE_NUMBER_REGEX.test(reNr) || INVOICE_NUMBER_LEGACY_REGEX.test(reNr);
}
function parseLeistungsmonatFromReNr(reNr) {
  if (!matchesAnyInvoiceFormat(reNr)) return null;
  const parts = reNr.split("-");
  if (parts.length !== 3) return null;
  const mmjj = parts[1];
  const monat = parseInt(mmjj.slice(0, 2), 10);
  const jahr = 2e3 + parseInt(mmjj.slice(2, 4), 10);
  if (!Number.isFinite(monat) || monat < 1 || monat > 12) return null;
  if (!Number.isFinite(jahr)) return null;
  return { jahr, monat };
}

// src/utils/invoice-status.ts
function computeInvoiceStatus(row, now = /* @__PURE__ */ new Date()) {
  if (row.storniert) return "storniert";
  if (row.eingang) return "bezahlt";
  if (row.faellig) {
    const faellig = row.faellig instanceof Date ? row.faellig : new Date(row.faellig);
    if (faellig < now) return "ueberfaellig";
  }
  return "offen";
}
function invoiceStatusSortKey(status) {
  switch (status) {
    case "ueberfaellig":
      return 0;
    case "offen":
      return 1;
    case "bezahlt":
      return 2;
    case "storniert":
      return 3;
  }
}

// src/utils/kontingent.ts
function normalizeEinheit(einheit) {
  const e = einheit.trim().toLowerCase();
  if ([
    "tag",
    "tage",
    "pt",
    "personentag",
    "personentage",
    "mannstag",
    "mannstage",
    "day",
    "days"
  ].includes(e))
    return "tage";
  if (["stunde", "stunden", "h", "std", "std.", "hur", "hour", "hours"].includes(e))
    return "stunden";
  if (["pauschal", "pauschale", "ls"].includes(e)) return "pauschal";
  if (["st\xFCck", "stueck", "stk", "stk.", "c62", "ea"].includes(e)) return "stueck";
  return "unknown";
}
function verbrauchAusPositionen(positionen) {
  const result = { tage: 0, stunden: 0, euro: 0 };
  if (!positionen) return result;
  for (const p of positionen) {
    const einheit = normalizeEinheit(p.einheit);
    const menge = Number(p.menge) || 0;
    const netto = Number(p.netto) || 0;
    const signedMenge = netto < 0 && menge > 0 ? -menge : menge;
    if (einheit === "tage") result.tage += signedMenge;
    else if (einheit === "stunden") result.stunden += signedMenge;
    result.euro += netto;
  }
  return result;
}
function toDate(d) {
  return typeof d === "string" ? new Date(d) : d;
}
function bereicheUeberlappen(vonA, bisA, vonB, bisB) {
  return vonA <= bisB && bisA >= vonB;
}
function aggregateKontingent(invoices, laufzeit) {
  const total = { tage: 0, stunden: 0, euro: 0 };
  const laufzeitVon = laufzeit?.von ? toDate(laufzeit.von) : null;
  const laufzeitBis = laufzeit?.bis ? toDate(laufzeit.bis) : null;
  for (const inv of invoices) {
    if (inv.storniert) continue;
    if (laufzeitVon && laufzeitBis) {
      const vonRoh = inv.zeitraumVon ?? inv.datum;
      const bisRoh = inv.zeitraumBis ?? inv.datum;
      if (vonRoh && bisRoh) {
        const von = toDate(vonRoh);
        const bis = toDate(bisRoh);
        if (!bereicheUeberlappen(von, bis, laufzeitVon, laufzeitBis)) continue;
      }
    }
    const v = verbrauchAusPositionen(inv.positionen);
    if ((!inv.positionen || inv.positionen.length === 0) && typeof inv.netto === "number") {
      v.euro = inv.netto;
    }
    total.tage += v.tage;
    total.stunden += v.stunden;
    total.euro += v.euro;
  }
  return total;
}
function kontingentRest(kontingentTyp, kontingentGesamt, verbrauch, gutschriftVerbrauch = { tage: 0, stunden: 0, euro: 0 }) {
  if (kontingentTyp === "keins") return null;
  const nettoTage = Math.max(0, verbrauch.tage - gutschriftVerbrauch.tage);
  const nettoStunden = Math.max(0, verbrauch.stunden - gutschriftVerbrauch.stunden);
  if (kontingentTyp === "tage") {
    const verbrauchtTage = nettoTage + nettoStunden / 8;
    return kontingentGesamt - verbrauchtTage;
  }
  if (kontingentTyp === "stunden") {
    const verbrauchtStunden = nettoStunden + nettoTage * 8;
    return kontingentGesamt - verbrauchtStunden;
  }
  if (kontingentTyp === "euro") {
    const nettoEuro = Math.max(0, verbrauch.euro - gutschriftVerbrauch.euro);
    return kontingentGesamt - nettoEuro;
  }
  return null;
}
function kontingentErschoepft(kontingentTyp, kontingentGesamt, rest) {
  if (kontingentTyp === "keins" || rest === null || !(kontingentGesamt > 0)) return false;
  return Math.round(rest * 100) / 100 <= 0;
}
function formatKontingent(kontingentTyp, wert) {
  if (kontingentTyp === "keins" || wert === null) return "\u2014";
  const rounded = Math.round(wert * 100) / 100;
  if (kontingentTyp === "tage") return `${rounded} Tage`;
  if (kontingentTyp === "stunden") return `${rounded} h`;
  if (kontingentTyp === "euro") {
    return new Intl.NumberFormat(LOCALE_DE, { style: "currency", currency: CURRENCY_EUR }).format(
      rounded
    );
  }
  return "\u2014";
}
function tageZuStunden(tage) {
  return tage * (STUNDEN_PRO_MONAT / TAGE_PRO_MONAT);
}
function stundenZuTage(stunden) {
  return stunden / (STUNDEN_PRO_MONAT / TAGE_PRO_MONAT);
}

// src/utils/liquiditaet.ts
var LIQUIDITAET_GEWICHTUNG_MAHNSTUFE = {
  0: 0.9,
  // überfällig, aber noch keine Mahnung erstellt
  1: 0.75,
  // Zahlungserinnerung
  2: 0.5,
  // 1. Mahnung
  3: 0.25
  // 2. Mahnung — kurz vor Inkasso
};
function eintrittswahrscheinlichkeit(tageAbHeute, mahnstufe) {
  if (tageAbHeute >= 0) return 1;
  const stufe = Math.min(Math.max(Math.trunc(mahnstufe), 0), 3);
  return LIQUIDITAET_GEWICHTUNG_MAHNSTUFE[stufe];
}

// src/utils/revenue.ts
var REVENUE_BEREICHE = ["VS1", "VS2", "VS3", "KEIN", "OFFEN"];
function toDate2(d) {
  return typeof d === "string" ? new Date(d) : d;
}
function stripTime(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function revenueInRange(invoice, rangeStart, rangeEnd) {
  if (invoice.storniert) return 0;
  const netto = invoice.netto ?? 0;
  if (netto === 0) return 0;
  if (!invoice.zeitraumVon || !invoice.zeitraumBis) {
    const d = stripTime(toDate2(invoice.datum));
    return d >= rangeStart && d <= rangeEnd ? netto : 0;
  }
  const vonDate = toDate2(invoice.zeitraumVon);
  const bisDate = toDate2(invoice.zeitraumBis);
  if (Number.isNaN(vonDate.getTime()) || Number.isNaN(bisDate.getTime())) return 0;
  const overlapStart = vonDate > rangeStart ? vonDate : rangeStart;
  const overlapEnd = bisDate < rangeEnd ? bisDate : rangeEnd;
  if (overlapEnd < overlapStart) return 0;
  const overlapDays = Math.round((overlapEnd.getTime() - overlapStart.getTime()) / (24 * 3600 * 1e3)) + 1;
  const totalDays = Math.round((bisDate.getTime() - vonDate.getTime()) / (24 * 3600 * 1e3)) + 1;
  if (totalDays <= 0) return 0;
  return netto * (overlapDays / totalDays);
}
function revenueInMonth(invoice, yearMonth) {
  const [yStr, mStr] = yearMonth.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10) - 1;
  const monthStart = new Date(Date.UTC(y, m, 1));
  const monthEnd = new Date(Date.UTC(y, m + 1, 0));
  return revenueInRange(invoice, monthStart, monthEnd);
}
function totalRevenueInMonth(invoices, yearMonth) {
  return invoices.reduce((sum, inv) => sum + revenueInMonth(inv, yearMonth), 0);
}
function filterNachBereich(invoices, bereich) {
  if (!bereich) return invoices;
  return invoices.filter((i) => (i.bereich ?? "OFFEN") === bereich);
}
function umsatzNachBereich(invoices, summe) {
  const out = { VS1: 0, VS2: 0, VS3: 0, KEIN: 0, OFFEN: 0 };
  for (const b of REVENUE_BEREICHE) {
    out[b] = Math.round(summe(filterNachBereich(invoices, b)) * 100) / 100;
  }
  return out;
}
function ohneBereichSumme(nach) {
  return {
    gesamt: Math.round((nach.KEIN + nach.OFFEN) * 100) / 100,
    davonKein: nach.KEIN,
    davonNochNichtZugeordnet: nach.OFFEN
  };
}
function totalRevenueInYear(invoices, year) {
  let total = 0;
  for (let m = 1; m <= 12; m++) {
    total += totalRevenueInMonth(invoices, `${year}-${String(m).padStart(2, "0")}`);
  }
  return total;
}
function extrapolateYearRevenue(invoices, year, now) {
  if (year !== now.getFullYear()) return null;
  const abgeschlosseneMonate = now.getMonth() - 1;
  if (abgeschlosseneMonate <= 0) return null;
  let summe = 0;
  for (let m = 1; m <= abgeschlosseneMonate; m++) {
    summe += totalRevenueInMonth(invoices, `${year}-${String(m).padStart(2, "0")}`);
  }
  return Math.round(summe / abgeschlosseneMonate * 12);
}
function revenueTimeline(invoices, endYear, endMonth, length = 12, heute = /* @__PURE__ */ new Date(), vergleichVorjahr = false) {
  const heuteJahr = heute.getUTCFullYear();
  const heuteMonat = heute.getUTCMonth() + 1;
  const result = [];
  for (let i = length - 1; i >= 0; i--) {
    const date = new Date(Date.UTC(endYear, endMonth - 1 - i, 1));
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const yearMonth = `${year}-${String(month).padStart(2, "0")}`;
    const netto = totalRevenueInMonth(invoices, yearMonth);
    const label = date.toLocaleDateString(LOCALE_DE, { month: "short", year: "2-digit" });
    const istTeilmonat = year === heuteJahr && month === heuteMonat;
    const vorjahrNetto = vergleichVorjahr ? totalRevenueInMonth(invoices, `${year - 1}-${String(month).padStart(2, "0")}`) : null;
    result.push({ year, month, label, yearMonth, netto, istTeilmonat, vorjahrNetto });
  }
  return result;
}
function topCustomersByRevenue(invoices, year, limit = 5) {
  const byKunde = /* @__PURE__ */ new Map();
  for (let m = 1; m <= 12; m++) {
    const ym = `${year}-${String(m).padStart(2, "0")}`;
    for (const inv of invoices) {
      const anteil = revenueInMonth(inv, ym);
      if (anteil === 0) continue;
      const key = inv.kundeNr ?? inv.kunde ?? "unbekannt";
      const existing = byKunde.get(key);
      if (existing) {
        existing.netto += anteil;
      } else {
        byKunde.set(key, {
          kundeNr: inv.kundeNr ?? null,
          kunde: inv.kunde ?? "Unbekannt",
          netto: anteil,
          anzahl: 0
        });
      }
    }
  }
  for (const inv of invoices) {
    if (inv.storniert) continue;
    const key = inv.kundeNr ?? inv.kunde ?? "unbekannt";
    const entry = byKunde.get(key);
    if (entry) entry.anzahl++;
  }
  return Array.from(byKunde.values()).filter((e) => e.netto > 0).sort((a, b) => b.netto - a.netto).slice(0, limit).map((e) => ({ ...e, netto: Math.round(e.netto * 100) / 100 }));
}
function customerConcentration(topKunden, gesamtNetto) {
  if (gesamtNetto <= 0) return null;
  const top3 = topKunden.slice(0, 3).reduce((sum, k) => sum + k.netto, 0);
  const top5 = topKunden.slice(0, 5).reduce((sum, k) => sum + k.netto, 0);
  return {
    top3Prozent: Math.round(top3 / gesamtNetto * 1e3) / 10,
    top5Prozent: Math.round(top5 / gesamtNetto * 1e3) / 10
  };
}

// src/utils/skonto.ts
var SKONTO_FRIST_TAGE_DEFAULT = 14;
function berechneSkontoFrist(input) {
  const { skontoTage, zahlungsziel, settingsSkontoTageRaw } = input;
  const raw = typeof settingsSkontoTageRaw === "number" ? settingsSkontoTageRaw : settingsSkontoTageRaw ? Number.parseInt(settingsSkontoTageRaw, 10) : NaN;
  const settingsDefault = Number.isFinite(raw) && raw > 0 ? raw : SKONTO_FRIST_TAGE_DEFAULT;
  const fristRaw = skontoTage ?? settingsDefault;
  return zahlungsziel ? Math.min(fristRaw, zahlungsziel) : fristRaw;
}
function berechneSkontoBetrag(brutto, skonto) {
  const skontoBetrag = skonto > 0 ? brutto * skonto / 100 : 0;
  return { skontoBetrag, zahlbar: brutto - skontoBetrag };
}
function berechneSkonto(input) {
  return {
    ...berechneSkontoBetrag(input.brutto, input.skonto),
    skontoFristTage: berechneSkontoFrist(input)
  };
}
function liegtInnerhalbSkontoFrist(rechnungsdatum, zahlungsdatum, skontoFristTage) {
  const start = rechnungsdatum instanceof Date ? rechnungsdatum : new Date(rechnungsdatum);
  const zahlung = zahlungsdatum instanceof Date ? zahlungsdatum : new Date(zahlungsdatum);
  const deltaTage = (zahlung.getTime() - start.getTime()) / (24 * 3600 * 1e3);
  return deltaTage >= 0 && deltaTage <= skontoFristTage;
}

// src/constants/error-codes.ts
var ERROR_CODES = {
  // 400 — Bad Request / Client-Fehler bei Eingabevalidierung
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INVALID_ID: "INVALID_ID",
  INVALID_PDF: "INVALID_PDF",
  INVALID_XML: "INVALID_XML",
  INVALID_JSON: "INVALID_JSON",
  INVALID_STUFE: "INVALID_STUFE",
  NOT_A_PDF: "NOT_A_PDF",
  NO_EMBEDDED_XML: "NO_EMBEDDED_XML",
  NO_RECIPIENT: "NO_RECIPIENT",
  MISSING_CODE: "MISSING_CODE",
  LAST_ADMIN: "LAST_ADMIN",
  // 401 — Authentifizierung fehlt / falsch
  UNAUTHENTICATED: "UNAUTHENTICATED",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  INVALID_PASSWORD: "INVALID_PASSWORD",
  // 403 — Authentifiziert, aber Berechtigung fehlt
  FORBIDDEN: "FORBIDDEN",
  ACCOUNT_DISABLED: "ACCOUNT_DISABLED",
  CSRF_FAILED: "CSRF_FAILED",
  GOBD_LOCKED: "GOBD_LOCKED",
  MFA_SETUP_REQUIRED: "MFA_SETUP_REQUIRED",
  // 404 — Resource nicht gefunden (generisch, Resource im message)
  NOT_FOUND: "NOT_FOUND",
  // 409 — Konflikt mit aktuellem Resource-State
  DUPLICATE: "DUPLICATE",
  DUPLICATE_RENR: "DUPLICATE_RENR",
  EMAIL_TAKEN: "EMAIL_TAKEN",
  BEZAHLT: "BEZAHLT",
  STORNIERT: "STORNIERT",
  INACTIVE: "INACTIVE",
  EMPTY_DOCUMENT: "EMPTY_DOCUMENT",
  MAHNUNG_EXISTS: "MAHNUNG_EXISTS",
  NOT_CONNECTED: "NOT_CONNECTED",
  NO_CLIENT_ID: "NO_CLIENT_ID",
  PERSONALKOSTEN_QUELLE_BESTAETIGUNG_NOETIG: "PERSONALKOSTEN_QUELLE_BESTAETIGUNG_NOETIG",
  // 413 — Payload zu groß
  XML_TOO_LARGE: "XML_TOO_LARGE",
  PAYLOAD_TOO_LARGE: "PAYLOAD_TOO_LARGE",
  // 415 — Request-Kodierung nicht angenommen (BL-674: komprimierte Requests, `inflate: false`)
  UNSUPPORTED_ENCODING: "UNSUPPORTED_ENCODING",
  // 422 — Daten zwar formal ok, aber inhaltlich unprozessbar
  INVALID_INVOICE_DATA: "INVALID_INVOICE_DATA",
  // 429 — Rate Limit
  RATE_LIMITED: "RATE_LIMITED",
  // 500 — Interner Serverfehler (Default-Fallback)
  INTERNAL_ERROR: "INTERNAL_ERROR",
  TOTP_SECRET_UNREADABLE: "TOTP_SECRET_UNREADABLE",
  // 503 — Service unverfügbar (externe Abhängigkeit)
  SMTP_NOT_CONFIGURED: "SMTP_NOT_CONFIGURED"
};
export {
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_COUNT,
  ATTACHMENT_MAX_TOTAL_BYTES,
  ATTACHMENT_MIME_TYPES,
  AUFTRAGSWERT_QUELLEN,
  BEREICHE,
  BEREICH_LABEL,
  BWA_KONTEN_MAP,
  CI,
  COMPANY,
  COMPANY_IDENTIFIERS,
  CURRENCY_EUR,
  DEFAULT_PAGE_SIZE,
  DEFAULT_WAEHRUNG,
  DEFAULT_ZAHLUNGSZIEL,
  EINGANG_IN_ZUKUNFT_MELDUNG,
  ERROR_CODES,
  EXPENSE_KATEGORIE_MAP,
  EXPENSE_KATEGORIE_SONSTIGES,
  FAELLIGKEITS_SCHWELLE_TAGE,
  FolgeVerschiebung,
  GESCHAEFTSBEREICHE,
  GESCHAEFTSBEREICH_KEIN,
  GESCHAEFTSBEREICH_KEIN_HINWEIS,
  GESCHAEFTSBEREICH_KEIN_LABEL,
  GESCHAEFTSBEREICH_LABEL,
  GUTSCHRIFT_BETRAEGE_MELDUNG,
  GUTSCHRIFT_BETRAGS_TOLERANZ,
  INVOICE_FORMATS,
  INVOICE_NUMBER_LEGACY_REGEX,
  INVOICE_NUMBER_MANUAL_REGEX,
  INVOICE_NUMBER_REGEX,
  LANGZEITKRANK_SCHWELLE_TAGE,
  LIQUIDITAET_GEWICHTUNG_MAHNSTUFE,
  LOBU_QUELLEN,
  LOCALE_DE,
  MAHNSTUFEN,
  MAX_PAGE_SIZE,
  MWST_SAETZE,
  OPOS_QUELLEN,
  OPOS_SECTION_TYPES,
  POSITION_KEY_MAP,
  PROJECT_STATUS,
  PROJECT_STATUS_LABEL,
  PROJECT_STATUS_NEU,
  RECHNUNGSTEXT_MAX_LAENGE,
  RECURRING_INTERVALS,
  REVENUE_BEREICHE,
  SONSTIGE_BETRIEBLICHE_ERLOESE_KONTEN,
  STANDARD_MWST,
  STUNDEN_PRO_MONAT,
  SUSA_PERIODEN_TYPEN,
  SUSA_QUELLEN,
  SUSA_SECTION_TYPES,
  TAGE_PRO_MONAT,
  TIMEZONE_BERLIN,
  UMSATZERLOESE_KONTEN,
  UNIT_CODE_MAP,
  USER_ROLES,
  USTVA_QUELLEN,
  VERMITTLER_UMSATZ_ERFASST_AB_JAHR,
  WAEHRUNGEN,
  WRITE_ROLES,
  aggregateKontingent,
  assertSafeInvoiceNumberParam,
  bankDetailsSchema,
  bankVerwendungSchema,
  berechneAuslastungFaktor,
  berechneSkonto,
  berechneSkontoBetrag,
  berechneSkontoFrist,
  berechneterStatus,
  bereinigeRechnungstext,
  bestimmeAuftragswert,
  bulkAbsenceBodySchema,
  bulkAbsenceEntrySchema,
  categorizeErloesKonto,
  categorizeKonto,
  changePasswordSchema,
  checkConsistency,
  computeInvoiceStatus,
  computePosition,
  computePositions,
  consultantCreateSchema,
  consultantUpdateSchema,
  customerConcentration,
  customerCreateSchema,
  customerUpdateSchema,
  daysInMonth,
  eintrittswahrscheinlichkeit,
  escapeXml,
  estimateProjectPeriodNetto,
  extrapolateYearRevenue,
  filterNachBereich,
  formatBerlinDateTime,
  formatInvoiceNumber,
  formatKontingent,
  formatProzent,
  geschaeftsbereichLabel,
  getArbeitstageImMonat,
  getBillingPeriodsForProject,
  getKundenKuerzel,
  getPeriodStatus,
  gezeichnetWirdAktiv,
  gutschriftBetraegeWiderspruechlich,
  gutschriftCreateSchema,
  gutschriftPositionSchema,
  gutschriftUpdateSchema,
  heuteInBerlin,
  invoiceAttachmentSchema,
  invoiceCreateSchema,
  invoiceFormatSchema,
  invoicePaymentSchema,
  invoicePositionSchema,
  invoicePreviewSchema,
  invoiceStatusSortKey,
  invoiceStornoSchema,
  invoiceUpdateSchema,
  isConsistent,
  isErloesKonto,
  isMaterialKonto,
  isSafeInvoiceNumberParam,
  isValidInvoiceNumber,
  istEingangInZukunft,
  istLangzeitkrankAusgenommen,
  klassifiziereSachkonto,
  kontingentErschoepft,
  kontingentRest,
  liegtInnerhalbSkontoFrist,
  listQuerySchema,
  lobuImportCreateSchema,
  lobuPositionInputSchema,
  lobuVoidSchema,
  loginSchema,
  matchesAnyInvoiceFormat,
  monthlyMetricUpsertSchema,
  normalizeEinheit,
  ohneBereichSumme,
  oposImportCreateSchema,
  oposPositionInputSchema,
  oposVoidSchema,
  parseInvoiceNumber,
  parseLeistungsmonatFromReNr,
  projectCreateSchema,
  projectStatusLabel,
  projectUpdateSchema,
  projektStatusWarnung,
  pruefeFirmenKennung,
  rechnungsEmailVon,
  registerSchema,
  revenueInMonth,
  revenueInRange,
  revenueTimeline,
  roleCreateSchema,
  roleUpdateSchema,
  roundMoney,
  statusIstManuell,
  strasseMitHausnummer,
  stundenZuTage,
  suggestBruttoFix,
  suggestMwstFix,
  sumPositions,
  susaImportCreateSchema,
  susaPositionInputSchema,
  susaVoidSchema,
  tageZuStunden,
  topCustomersByRevenue,
  totalRevenueInMonth,
  totalRevenueInYear,
  totpCodeSchema,
  twoFaConfirmSchema,
  twoFaDisableSchema,
  twoFaVerifySchema,
  umsatzNachBereich,
  ustvaImportCreateSchema,
  ustvaVoidSchema,
  verbrauchAusPositionen,
  verschiebeDatumUmEinenMonat,
  verschiebePositionsZeitraum,
  verschiebeZeitraumFuerFolgerechnung
};
